import React, { useState, useEffect } from 'react';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useBalance, useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { formatEther, parseEther } from 'viem';
import FeeEscrowABI from '../abi/FeeEscrow.abi.json';
import MockDeFiLendingProtocolABI from '../abi/MockDeFiLendingProtocol.abi.json';
import { getEnvConfig } from '../config/env';

// Get contract addresses from environment configuration
const envConfig = getEnvConfig();
const FEE_ESCROW_CONTRACT_ADDRESS = envConfig.contracts.feeEscrow || "0x0000000000000000000000000000000000000000";
const DAPP_CONTRACT_ADDRESS = envConfig.contracts.dapp || "0x0000000000000000000000000000000000000000";

const FeeEscrowManagement: React.FC = () => {
  const { address, isConnected, chain } = useAccount();

  // Form stateI
  const [depositAmount, setDepositAmount] = useState('');
  const [depositForAddress, setDepositForAddress] = useState(DAPP_CONTRACT_ADDRESS);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawRecipient, setWithdrawRecipient] = useState('');

  // UI states
  const [depositError, setDepositError] = useState<string | null>(null);
  const [depositSuccess, setDepositSuccess] = useState<string | null>(null);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const [withdrawSuccess, setWithdrawSuccess] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelSuccess, setCancelSuccess] = useState<string | null>(null);
  const [copiedRequestId, setCopiedRequestId] = useState(false);

  // Get balance for the connected wallet
  const { data: balanceData } = useBalance({
    address: address,
  });

  // Read balance info from contract
  const { data: balanceInfo, refetch: refetchBalanceInfo } = useReadContract({
    address: FEE_ESCROW_CONTRACT_ADDRESS as `0x${string}`,
    abi: FeeEscrowABI,
    functionName: 'getBalanceInfo',
    args: [DAPP_CONTRACT_ADDRESS as `0x${string}`],
  });

  // Extract balance values with proper typing
  const totalBalance = balanceInfo ? (balanceInfo as readonly [bigint, bigint, bigint])[0] : BigInt(0);
  const reservedAmount = balanceInfo ? (balanceInfo as readonly [bigint, bigint, bigint])[1] : BigInt(0);
  const availableAmount = balanceInfo ? (balanceInfo as readonly [bigint, bigint, bigint])[2] : BigInt(0);

  // Read pending withdrawal request ID for the DApp contract using the getter function
  const { data: pendingRequestId, refetch: refetchPendingRequest } = useReadContract({
    address: FEE_ESCROW_CONTRACT_ADDRESS as `0x${string}`,
    abi: FeeEscrowABI,
    functionName: 'getPendingWithdrawalRequest',
    args: [DAPP_CONTRACT_ADDRESS as `0x${string}`],
    query: { enabled: Boolean(isConnected) }
  });

  // Read withdrawal request details if there's a pending request
  const { data: withdrawalRequestData, refetch: refetchWithdrawalRequest } = useReadContract({
    address: FEE_ESCROW_CONTRACT_ADDRESS as `0x${string}`,
    abi: FeeEscrowABI,
    functionName: 'getWithdrawalRequest',
    args: [pendingRequestId as bigint],
    query: { enabled: Boolean(pendingRequestId && Number(pendingRequestId) > 0) }
  });

  // Write contract hooks for depositFor
  const { writeContractAsync: writeDepositFor, data: depositForTxHash, isPending: isDepositForPending } = useWriteContract();

  // Write contract hooks for requestWithdrawal
  const { writeContract: writeRequestWithdrawal, data: requestWithdrawalTxHash, isPending: isRequestWithdrawalPending } = useWriteContract();

  // Write contract hooks for cancelWithdrawalRequest
  const { writeContract: writeCancelWithdrawal, data: cancelWithdrawalTxHash, isPending: isCancelWithdrawalPending } = useWriteContract();

  // Wait for depositFor transaction
  const { isSuccess: depositForTxSuccess, isLoading: depositForTxLoading } = useWaitForTransactionReceipt({
    hash: depositForTxHash,
  });

  // Wait for requestWithdrawal transaction
  const { isSuccess: requestWithdrawalTxSuccess, isLoading: requestWithdrawalTxLoading } = useWaitForTransactionReceipt({
    hash: requestWithdrawalTxHash,
  });

  // Wait for cancelWithdrawal transaction
  const { isSuccess: cancelWithdrawalTxSuccess, isLoading: cancelWithdrawalTxLoading } = useWaitForTransactionReceipt({
    hash: cancelWithdrawalTxHash,
  });

  // Handle successful depositFor
  useEffect(() => {
    if (depositForTxSuccess && depositForTxHash) {
      setDepositSuccess(`https://humanity-testnet.explorer.alchemy.com/tx/${depositForTxHash}`);
      setDepositAmount('');
      setDepositForAddress(DAPP_CONTRACT_ADDRESS);
      setDepositError(null);
      refetchBalanceInfo();
    }
  }, [depositForTxSuccess, depositForTxHash, refetchBalanceInfo]);

  // Handle successful requestWithdrawal
  useEffect(() => {
    if (requestWithdrawalTxSuccess && requestWithdrawalTxHash) {
      setWithdrawSuccess(`https://humanity-testnet.explorer.alchemy.com/tx/${requestWithdrawalTxHash}`);
      setWithdrawAmount('');
      setWithdrawRecipient('');
      setWithdrawError(null);
      refetchBalanceInfo();
      refetchPendingRequest();
    }
  }, [requestWithdrawalTxSuccess, requestWithdrawalTxHash, refetchBalanceInfo, refetchPendingRequest]);

  // Handle successful cancelWithdrawal
  useEffect(() => {
    if (cancelWithdrawalTxSuccess && cancelWithdrawalTxHash) {
      setCancelSuccess(`https://humanity-testnet.explorer.alchemy.com/tx/${cancelWithdrawalTxHash}`);
      setCancelError(null);
      refetchBalanceInfo();
      refetchPendingRequest();
    }
  }, [cancelWithdrawalTxSuccess, cancelWithdrawalTxHash, refetchBalanceInfo, refetchPendingRequest]);

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  // Handle copy request ID
  const handleCopyRequestId = () => {
    if (pendingRequestId) {
      navigator.clipboard.writeText(pendingRequestId.toString());
      setCopiedRequestId(true);
      setTimeout(() => setCopiedRequestId(false), 2000);
    }
  };

  // Handle deposit
  const handleDeposit = () => {
    console.log("🚀 ~ handleDeposit ~ handleDeposit:")

    setDepositError(null);
    setDepositSuccess(null);

    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      setDepositError('Please enter a valid amount');
      return;
    }

    try {
      const value = parseEther(depositAmount);
      const targetAddress = depositForAddress && depositForAddress.trim() !== ''
        ? depositForAddress
        : address;

      if (!targetAddress) {
        setDepositError('Deposit address is required');
        return;
      }

      const result = writeDepositFor({
        address: FEE_ESCROW_CONTRACT_ADDRESS as `0x${string}`,
        abi: FeeEscrowABI,
        functionName: 'deposit',
        args: [targetAddress as `0x${string}`],
        value: value,
      });
      console.log("🚀 ~ handleDeposit ~ result:", result)
    } catch (error) {
      setDepositError('Failed to initiate deposit transaction');
      console.error(error);
    }
  };

  // Handle request withdrawal
  const handleRequestWithdrawal = () => {
    setWithdrawError(null);
    setWithdrawSuccess(null);

    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      setWithdrawError('Please enter a valid amount');
      return;
    }

    const recipient = withdrawRecipient && withdrawRecipient.trim() !== ''
      ? withdrawRecipient
      : address;

    if (!recipient) {
      setWithdrawError('Recipient address is required');
      return;
    }

    try {
      const amount = parseEther(withdrawAmount);

      writeRequestWithdrawal({
        address: DAPP_CONTRACT_ADDRESS as `0x${string}`,
        abi: MockDeFiLendingProtocolABI,
        functionName: 'requestFeeEscrowWithdrawal',
        args: [recipient as `0x${string}`, amount],
      });
    } catch (error) {
      setWithdrawError('Failed to initiate withdrawal request');
      console.error(error);
    }
  };

  // Handle cancel withdrawal request
  const handleCancelWithdrawal = () => {
    setCancelError(null);
    setCancelSuccess(null);

    if (!pendingRequestId || Number(pendingRequestId) === 0) {
      setCancelError('No pending withdrawal request to cancel');
      return;
    }

    try {
      writeCancelWithdrawal({
        address: DAPP_CONTRACT_ADDRESS as `0x${string}`,
        abi: MockDeFiLendingProtocolABI,
        functionName: 'cancelFeeEscrowWithdrawalRequest',
        args: [pendingRequestId as bigint],
      });
    } catch (error) {
      setCancelError('Failed to cancel withdrawal request');
      console.error(error);
    }
  };

  // Clear success messages after 10 seconds
  useEffect(() => {
    if (depositSuccess) {
      const timer = setTimeout(() => setDepositSuccess(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [depositSuccess]);

  useEffect(() => {
    if (withdrawSuccess) {
      const timer = setTimeout(() => setWithdrawSuccess(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [withdrawSuccess]);

  useEffect(() => {
    if (cancelSuccess) {
      const timer = setTimeout(() => setCancelSuccess(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [cancelSuccess]);

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Fee Escrow Management</h1>
        <ConnectButton />
      </div>

      {/* Wallet Information Card */}
      {isConnected && address && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Wallet Information</h2>

          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="text-gray-600 font-medium">Address:</span>
              <span className="font-mono text-sm">{formatAddress(address)}</span>
            </div>

            {chain && (
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <span className="text-gray-600 font-medium">Network:</span>
                <span className="font-semibold">{chain.name}</span>
              </div>
            )}

            <div className="flex justify-between items-center p-3 bg-purple-50 rounded">
              <span className="text-gray-600 font-medium">DApp Contract:</span>
              <span className="font-mono text-xs text-purple-600">{formatAddress(DAPP_CONTRACT_ADDRESS)}</span>
            </div>

            {balanceData && (
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <span className="text-gray-600 font-medium">Wallet Balance:</span>
                <span className="font-semibold">
                  {parseFloat(balanceData.formatted).toFixed(4)} {balanceData.symbol}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {!isConnected ? (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">Please connect your wallet to access Fee Escrow features</p>
            <ConnectButton />
          </div>
        </div>
      ) : (
        <>
          {/* Escrow Balance Info */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Escrow Balance Information</h2>

            {balanceInfo ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                  <span className="text-gray-600 font-medium">Total Balance:</span>
                  <span className="font-semibold text-blue-600">
                    {formatEther(totalBalance)} tHp
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-yellow-50 rounded">
                  <span className="text-gray-600 font-medium">Reserved Amount:</span>
                  <span className="font-semibold text-yellow-600">
                    {formatEther(reservedAmount)} tHp
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                  <span className="text-gray-600 font-medium">Available Amount:</span>
                  <span className="font-semibold text-green-600">
                    {formatEther(availableAmount)} tHp
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-500">Loading balance information...</p>
              </div>
            )}
          </div>

          {/* Deposit Section */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Deposit to Escrow</h2>

            <div className="space-y-4">
              <div>
                <label htmlFor="deposit-amount" className="block text-sm font-medium text-gray-700 mb-2">
                  Amount (tHp)
                </label>
                <input
                  id="deposit-amount"
                  type="number"
                  step="0.000001"
                  placeholder="0.0"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="deposit-for-address" className="block text-sm font-medium text-gray-700 mb-2">
                  Deposit For Address (Optional - leave empty to deposit for yourself)
                </label>
                <input
                  id="deposit-for-address"
                  type="text"
                  placeholder="0x..."
                  value={depositForAddress}
                  onChange={(e) => setDepositForAddress(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {depositError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{depositError}</p>
                </div>
              )}

              {depositSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-600 text-sm">
                    Deposit successful! View transaction:{' '}
                    <a
                      href={depositSuccess}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-semibold"
                    >
                      Explorer
                    </a>
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={handleDeposit}
                disabled={isDepositForPending || depositForTxLoading}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {(isDepositForPending || depositForTxLoading) ? 'Processing...' : 'Deposit'}
              </button>
            </div>
          </div>

          {/* Withdraw Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Request a Withdrawal</h2>

            {/* Show pending withdrawal request if exists */}
            {pendingRequestId && Number(pendingRequestId) > 0 && withdrawalRequestData ? (
              <div className="space-y-4">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h3 className="font-semibold text-yellow-800 mb-3">Pending Withdrawal Request</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Request ID:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold">#{pendingRequestId.toString()}</span>
                        <button
                          type="button"
                          onClick={handleCopyRequestId}
                          className="p-1 hover:bg-yellow-100 rounded transition-colors"
                          title="Copy Request ID"
                        >
                          {copiedRequestId ? (
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Amount:</span>
                      <span className="font-semibold">{formatEther((withdrawalRequestData as readonly [string, string, bigint, bigint, number])[2])} tHp</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Recipient:</span>
                      <span className="font-mono text-xs">{formatAddress((withdrawalRequestData as readonly [string, string, bigint, bigint, number])[1])}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className="font-semibold text-yellow-600">
                        {(withdrawalRequestData as readonly [string, string, bigint, bigint, number])[4] === 0 ? 'Pending' :
                          (withdrawalRequestData as readonly [string, string, bigint, bigint, number])[4] === 1 ? 'Approved' :
                            (withdrawalRequestData as readonly [string, string, bigint, bigint, number])[4] === 2 ? 'Rejected' : 'Cancelled'}
                      </span>
                    </div>
                  </div>
                </div>

                {cancelError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{cancelError}</p>
                  </div>
                )}

                {cancelSuccess && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-600 text-sm">
                      Withdrawal request cancelled! View transaction:{' '}
                      <a
                        href={cancelSuccess}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline font-semibold"
                      >
                        Explorer
                      </a>
                    </p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleCancelWithdrawal}
                  disabled={isCancelWithdrawalPending || cancelWithdrawalTxLoading}
                  className="w-full bg-yellow-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-yellow-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {(isCancelWithdrawalPending || cancelWithdrawalTxLoading) ? 'Cancelling...' : 'Cancel Withdrawal Request'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label htmlFor="withdraw-amount" className="block text-sm font-medium text-gray-700 mb-2">
                    Amount (tHp)
                  </label>
                  <input
                    id="withdraw-amount"
                    type="number"
                    step="0.000001"
                    placeholder="0.0"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="withdraw-recipient" className="block text-sm font-medium text-gray-700 mb-2">
                    Recipient Address (Optional - leave empty for your address)
                  </label>
                  <input
                    id="withdraw-recipient"
                    type="text"
                    placeholder="0x..."
                    value={withdrawRecipient}
                    onChange={(e) => setWithdrawRecipient(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {!!Number(availableAmount) && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">
                      Available to withdraw: <span className="font-semibold">{formatEther(availableAmount)} tHp</span>
                    </p>
                  </div>
                )}

                {withdrawError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{withdrawError}</p>
                  </div>
                )}

                {withdrawSuccess && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-600 text-sm">
                      Withdrawal request submitted! View transaction:{' '}
                      <a
                        href={withdrawSuccess}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline font-semibold"
                      >
                        Explorer
                      </a>
                    </p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleRequestWithdrawal}
                  disabled={isRequestWithdrawalPending || requestWithdrawalTxLoading}
                  className="w-full bg-red-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {(isRequestWithdrawalPending || requestWithdrawalTxLoading) ? 'Processing...' : 'Request Withdrawal'}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FeeEscrowManagement;
